import React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import { StyledPaper } from "../../customComponents/styled/styledPaper";
import { useFormik } from "formik";
import * as yup from "yup";
import { StyledTextField } from "../../customComponents/styled/styledInputs";
import { useSetInitialPasswordMutation } from "../../../app/authApiSlice";
import { useAppDispatch } from "../../../app/store";
import { setUser } from "./authSlice";
import { useNavigate } from "react-router-dom";
import toast from "../../../helpers/toast";

const PASSWORD_REQUIREMENTS = [
  "At least 8 characters",
];

const passwordSchema = yup
  .string()
  .required("New password is required")
  .min(8, "At least 8 characters");

function ForcePasswordChange() {
  const [setInitialPassword, { isLoading }] = useSetInitialPasswordMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: yup.object({
      newPassword: passwordSchema,
      confirmPassword: yup
        .string()
        .required("Please confirm your password")
        .oneOf([yup.ref("newPassword"), null], "Passwords do not match"),
    }),
    onSubmit: async (values) => {
      try {
        const response = await setInitialPassword({
          new_password: values.newPassword,
        }).unwrap();

        // Update the user in Redux so force_password_change is now false
        if (response?.user) {
          dispatch(setUser(response.user));
        } else {
          dispatch(setUser({ force_password_change: false }));
        }

        toast.success("Password updated successfully. Welcome!");
        navigate("/");
      } catch (error) {
        toast.error(error?.message || "Something went wrong. Please try again.");
      }
    },
  });

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#eee",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <StyledPaper>
        <Box className="paperBody" sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You must set a new password before continuing.
          </Alert>

          <Typography
            variant="h5"
            sx={{
              color: "#27ae60",
              fontWeight: "bold",
              mb: 0.5,
            }}
          >
            Set Your Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your account requires a password change. Please choose a strong password.
          </Typography>

          <Box
            sx={(theme) => ({
              background: theme.palette.mode === "dark" ? "#444" : "#f5f7fa",
              borderRadius: 1,
              p: 1.5,
              mb: 2,
            })}
          >
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              Password requirements:
            </Typography>
            <List dense disablePadding>
              {PASSWORD_REQUIREMENTS.map((req) => (
                <ListItem key={req} disableGutters sx={{ py: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    • {req}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{ "& > *": { mb: 2 } }}
          >
            <FormControl error fullWidth>
              <StyledTextField
                autoFocus
                type="password"
                variant="outlined"
                id="newPassword"
                name="newPassword"
                placeholder="New Password"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.newPassword &&
                  Boolean(formik.errors.newPassword)
                }
              />
              <FormHelperText>
                {formik.touched.newPassword && formik.errors.newPassword}
              </FormHelperText>
            </FormControl>

            <FormControl error fullWidth>
              <StyledTextField
                type="password"
                variant="outlined"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="Confirm New Password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword)
                }
              />
              <FormHelperText>
                {formik.touched.confirmPassword &&
                  formik.errors.confirmPassword}
              </FormHelperText>
            </FormControl>

            <Stack direction="row">
              {isLoading ? (
                <CircularProgress />
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  sx={{ background: "#27ae60", "&:hover": { background: "#219150" } }}
                >
                  Set Password
                </Button>
              )}
            </Stack>
          </Box>
        </Box>
      </StyledPaper>
    </Box>
  );
}

export default ForcePasswordChange;
